import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import { SystemApiService } from '@wdx/clmi/api-services/services';
import {
    Config,
    LocalStorageKey,
    LocalStorageService,
    ROUTE_PREFIX,
    WINDOW,
} from '@wdx/shared/utils';
import { BehaviorSubject, EMPTY, from, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { SESSION_IDLE_TIME_MINS } from '../constants/authentication.constants';
import { SystemSettingsService } from '@wdx/clmi/utils/services';
import { ConfigService } from './config.service';
import { sanitizeUrl } from '@braintree/sanitize-url';

export const HELP_CENTRE_PUBLIC_URL = 'https://support.clmi.io/hc';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService implements OnDestroy {
    private environment = inject(ConfigService);
    private router = inject(Router);
    private localStorageService = inject(LocalStorageService);
    private systemSettingsService = inject(SystemSettingsService);
    private systemApiService = inject(SystemApiService);

    config: Config = null;
    isAuthenticated$: Observable<boolean> = null;
    auth0Service: AuthService;
    private _window = inject(WINDOW);

    // Create subject and public observable of user profile data
    private userProfileSubject$ = new BehaviorSubject<any>(null);
    userProfile$ = this.userProfileSubject$.asObservable();

    // Create subject and public observable of user profile data
    private authenticatingSubject$ = new BehaviorSubject<boolean>(null);
    isAuthenticating$ = this.authenticatingSubject$.asObservable();

    // Create a local property for login status
    loggedIn: boolean = null;

    notifyTimeoutModal$ = new Subject<number>();
    destroyed$ = new Subject<boolean>();
    sessionExpiryTime: Date;
    interval: NodeJS.Timeout;

    constructor() {
        this.config = this.environment.getConfiguration();
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    initSessionCountDown() {
        this.setSessionExpiryTime();

        this.interval = setInterval(() => {
            const dateDiff: number = Math.floor(
                (this.sessionExpiryTime.getTime() - new Date().getTime()) /
                    1000,
            );

            if (dateDiff < 0) {
                this.logout();
            } else {
                this.notifyTimeoutModal$.next(dateDiff);
            }
        }, 1000);
    }

    // When calling, options can be passed if desired
    // https://auth0.github.io/auth0-spa-js/classes/auth0client.html#getuser
    getUser$(): Observable<any> {
        return this.auth0Service.user$.pipe(
            tap((user) => this.userProfileSubject$.next(user)),
        );
    }

    /**
     * Triggers loginWithRedirectLoginCall
     * @param returnTo An optional returnTo url. Note this is a tokenised.
     */
    login(options?: { returnToUrl?: string }) {
        // Take user to Auth0 to authenticate
        this.auth0Service.loginWithRedirect({
            appState: {
                target: ROUTE_PREFIX[0],
                ...(options?.returnToUrl && {
                    returnToUrl: options.returnToUrl,
                }),
            },
            authorizationParams: {
                redirect_uri: `${window.location.origin}`,
            },
        });
    }

    getAppState$() {
        return this.auth0Service.appState$;
    }

    setSessionExpiryTime(): void {
        // set the time the session will expire
        const IDLE_TIME_MINS = this.systemSettingsService?.get()
            ?.WebApplicationTimeoutSeconds
            ? this.systemSettingsService?.get().WebApplicationTimeoutSeconds /
              60
            : SESSION_IDLE_TIME_MINS;

        const expiresAt = new Date(
            IDLE_TIME_MINS * 60 * 1000 + new Date().getTime(),
        );

        this.localStorageService.setStringKey(
            `${LocalStorageKey.ExpiresAt}`,
            expiresAt.toString(),
        );

        this.sessionExpiryTime = expiresAt;
    }

    isAuthenticated(): boolean {
        const expiresAt: Date = new Date(
            this.localStorageService.getStringKey(
                `${LocalStorageKey.ExpiresAt}`,
            ),
        );

        return new Date() < expiresAt;
    }

    logout() {
        this.localStorageService.setStringKey(`${LocalStorageKey.ExpiresAt}`);
        // Ensure Auth0 client instance exists

        // Call method to log out
        this.auth0Service.logout({
            // eslint-disable-next-line camelcase
            clientId:
                this.environment.getConfiguration().AuthenticationClientId,
            logoutParams: {
                returnTo: `${window.location.origin}`,
            },
        });
    }

    getTokenSilently$(
        options?,
    ): Observable<string | GetTokenSilentlyVerboseResponse> {
        return this.auth0Service.getAccessTokenSilently(options).pipe(
            catchError((error) => {
                // Error recieved from Auth0 could be one of:
                // - login_required
                // - consent_required
                // - interaction_required

                // eslint-disable-next-line no-console
                console.error(
                    `Error getting token from Auth0: ${error.error} - ${error.error_description}`,
                );

                // In any case, we want to redirect user to go through authentication
                this.authenticate(this.router.url);
                return EMPTY;
            }),
        );
    }

    authenticate(redirectUrl: string) {
        // The authentication process at the moment involves starting at the welcome screen
        // and clicking the login button. In theory, we could skip this and go straight to Auth0
        // but we would need to then think about the behaviour of the log out button

        if (this.config.TestMode) {
            return;
        }

        // First check we are not already on the welcome screen
        if (!this.router.routerState.snapshot.url.startsWith('/')) {
            this.router.navigate(['/', { redirect: redirectUrl }]);
        }
    }

    isLoggedIn() {
        return this.loggedIn;
    }

    returnTo(
        returnToUrl: string,
        completeCallback: () => void = () => undefined,
    ): void {
        const sanitizedUrl = sanitizeUrl(returnToUrl);
        if (sanitizedUrl === 'about:blank') {
            completeCallback();
            return;
        }

        if (sanitizedUrl.toLowerCase().startsWith('/')) {
            this._window.location.replace(sanitizedUrl);
            completeCallback();
            return;
        }

        this.systemApiService
            .getSystemSettings()
            .pipe(
                take(1),
                map((settings) => settings.HelpCentreUrl),
                switchMap((helpCentreUrl) => {
                    if (
                        [HELP_CENTRE_PUBLIC_URL, helpCentreUrl].some(
                            (url) =>
                                url?.length &&
                                sanitizedUrl.toLowerCase().startsWith(url),
                        )
                    ) {
                        return this.systemApiService
                            .getTokenisedUrl(`"${helpCentreUrl}"`)
                            .pipe(
                                switchMap((tokeniseUrlasBlob: Blob | null) => {
                                    return tokeniseUrlasBlob
                                        ? from(
                                              new Response(
                                                  tokeniseUrlasBlob,
                                              ).text(),
                                          )?.pipe(
                                              map((text) => ({
                                                  value: text?.replaceAll(
                                                      '"',
                                                      '',
                                                  ),
                                                  type: 'tokenised',
                                              })),
                                              catchError(() => of(null)),
                                          )
                                        : of(null);
                                }),
                                catchError(() => of(null)),
                            );
                    }
                    return of({ value: sanitizedUrl, type: 'plain' });
                }),
            )
            .subscribe(
                (
                    url: { value: string; type: 'tokenised' | 'plain' } | null,
                ) => {
                    if (url?.type === 'tokenised') {
                        const hasDeepPath =
                            sanitizedUrl.replace(HELP_CENTRE_PUBLIC_URL, '')
                                .length > 1;
                        this._window.location.replace(
                            `${url.value}${hasDeepPath ? '&return_to=' + encodeURIComponent(sanitizedUrl) : ''}`,
                        );
                        return;
                    } else if (url?.type === 'plain') {
                        try {
                            const { hostname } = new URL(sanitizedUrl);
                            if (
                                hostname === 'clmi.io' ||
                                hostname.endsWith('.clmi.io')
                            ) {
                                this._window.location.replace(sanitizedUrl);
                            }
                        } catch {
                            return;
                        }
                    }
                    completeCallback();
                    this.router.navigate(ROUTE_PREFIX);
                },
            );
    }
}
